import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import InvoiceManagement, {
  InvoiceData,
} from "../components/invoiceManagement";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  User,
  Building,
  FileText,
  Edit,
  Clock,
  Target,
} from "lucide-react";

interface Project {
  _id: string;
  projectName: string;
  clientId: {
    _id: string;
    clientName: string;
    contactPerson: {
      name: string;
      email: string;
    };
  };
  projectDetails: {
    description: string;
    scope?: string;
    deliverables?: string[];
    technologies?: string[];
  };
  timeline: {
    startDate: string;
    estimatedEndDate: string;
    actualEndDate?: string;
  };
  paymentStructure: {
    totalAmount: number;
    currency: string;
    milestones: Array<{
      _id: string;
      name: string;
      percentage: number;
      amount: number;
      status: string;
      invoiceUrl?: string;
      invoiceNumber?: string;
      invoiceUploadedAt?: string;
    }>;
  };
  status: string;
  priority: string;
  progress?: {
    completionPercentage: number;
  };
  createdAt: string;
}

export default function ProjectDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (id) {
      fetchProjectDetails();
    }
  }, [id]);

  const fetchProjectDetails = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/projects/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setProject(response.data.data.project);
    } catch (error: any) {
      console.error("Error fetching project details:", error);
      setError(
        error.response?.data?.message || "Failed to fetch project details"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvoiceUpdate = (invoices: InvoiceData[]) => {
    console.log("Invoices updated:", invoices);
    // Optionally refresh project data or update local state
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      planning: { color: "bg-blue-100 text-blue-800", label: "Planning" },
      in_progress: {
        color: "bg-green-100 text-green-800",
        label: "In Progress",
      },
      on_hold: { color: "bg-yellow-100 text-yellow-800", label: "On Hold" },
      completed: { color: "bg-purple-100 text-purple-800", label: "Completed" },
      cancelled: { color: "bg-red-100 text-red-800", label: "Cancelled" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "bg-gray-100 text-gray-800",
      label: status,
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      critical: { color: "bg-red-100 text-red-800", label: "Critical" },
      high: { color: "bg-orange-100 text-orange-800", label: "High" },
      medium: { color: "bg-yellow-100 text-yellow-800", label: "Medium" },
      low: { color: "bg-green-100 text-green-800", label: "Low" },
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || {
      color: "bg-gray-100 text-gray-800",
      label: priority,
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <Spinner variant="ellipsis" size={40} className="text-primary" />
          <div className="text-center">
            <h3 className="text-lg font-medium">Loading Employees</h3>
            <p className="text-sm text-muted-foreground">
              Fetching employee data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <Button
            variant="ghost"
            onClick={() => navigate("/projects")}
            className="h-9 w-9 p-0"
            disabled={isLoading}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p>Project not found</p>
          <Button onClick={() => navigate("/projects")} className="mt-4">
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/projects")}
            className="h-9 w-9 p-0"
            disabled={isLoading}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{project.projectName}</h1>
            <p className="text-muted-foreground">
              Client: {project.clientId.clientName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/projects/edit/${project._id}`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Project
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                {getStatusBadge(project.status)}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-500" />
              <div>
                <p className="text-sm text-muted-foreground">Priority</p>
                {getPriorityBadge(project.priority)}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="font-semibold">
                  {formatCurrency(project.paymentStructure.totalAmount)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Milestones</p>
                <p className="font-semibold">
                  {project.paymentStructure.milestones.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices">Invoice Management</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Project Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Project Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Description
                  </label>
                  <p className="mt-1">{project.projectDetails.description}</p>
                </div>
                {project.projectDetails.scope && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Scope
                    </label>
                    <p className="mt-1">{project.projectDetails.scope}</p>
                  </div>
                )}
                {project.projectDetails.technologies &&
                  project.projectDetails.technologies.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Technologies
                      </label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {project.projectDetails.technologies.map(
                          (tech, index) => (
                            <Badge key={index} variant="secondary">
                              {tech}
                            </Badge>
                          )
                        )}
                      </div>
                    </div>
                  )}
              </CardContent>
            </Card>

            {/* Client Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Client Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Client Name
                  </label>
                  <p className="mt-1 font-semibold">
                    {project.clientId.clientName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Contact Person
                  </label>
                  <div className="mt-1">
                    <p className="font-medium">
                      {project.clientId.contactPerson.name}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {project.clientId.contactPerson.email}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payment Structure
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total Amount:</span>
                  <span className="text-lg font-bold">
                    {formatCurrency(project.paymentStructure.totalAmount)}
                  </span>
                </div>
                <div className="space-y-2">
                  {project.paymentStructure.milestones.map(
                    (milestone, index) => (
                      <div
                        key={milestone._id}
                        className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <span className="font-medium">{milestone.name}</span>
                          <span className="text-sm text-muted-foreground ml-2">
                            ({milestone.percentage}%)
                          </span>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold">
                            {formatCurrency(milestone.amount)}
                          </div>
                          <div className="text-xs text-muted-foreground capitalize">
                            {milestone.status}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <InvoiceManagement
            milestones={project.paymentStructure.milestones.map((m) => ({
              id: m._id,
              name: m.name,
              percentage: m.percentage,
            }))}
            totalAmount={project.paymentStructure.totalAmount}
            clientName={project.clientId.clientName}
            projectName={project.projectName}
            projectId={project._id}
            onInvoiceUpdate={handleInvoiceUpdate}
            initialInvoices={project.paymentStructure.milestones.map((m) => ({
              id: m._id,
              milestoneName: m.name,
              amount: m.amount,
              percentage: m.percentage,
              status: m.invoiceUrl ? "uploaded" : "pending",
              invoiceUrl: m.invoiceUrl,
              invoiceNumber: m.invoiceNumber,
              uploadedAt: m.invoiceUploadedAt,
            }))}
          />
        </TabsContent>

        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Project Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Start Date
                  </label>
                  <p className="mt-1">
                    {new Date(project.timeline.startDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Estimated End Date
                  </label>
                  <p className="mt-1">
                    {new Date(
                      project.timeline.estimatedEndDate
                    ).toLocaleDateString()}
                  </p>
                </div>
                {project.timeline.actualEndDate && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Actual End Date
                    </label>
                    <p className="mt-1">
                      {new Date(
                        project.timeline.actualEndDate
                      ).toLocaleDateString()}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Created
                  </label>
                  <p className="mt-1">
                    {new Date(project.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
